import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { takeHomeScreenshot, useHistoricalResultsQuery } from '@/api/vercel';
import { tallyResultsForToday } from '@/utils/tally-results';
import TrophyIcon from '@/components/icons/trophy-icon';
import MedalIcon from '@/components/icons/medal-icon';
import { format } from 'date-fns';

function DailyWrap() {
  const [searchParams] = useSearchParams();
  const [imgError, setImgError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [screenshotSrc, setScreenshotSrc] = useState('');
  const [winsCount, setWinsCount] = useState('');
  const [placesCount, setPlacesCount] = useState('');
  
  // Get URL parameters
  const paramWins = searchParams.get('wins');
  const paramPlaces = searchParams.get('places');
  const paramScreenshotUrl = searchParams.get('screenshotUrl');
  const previewToken = searchParams.get('preview_token');
  
  // Get today's date in the required format
  const today = format(new Date(), "dd-MM-yyyy");
  
  // Fetch historical results data
  const { 
    data: histResults = { data: {} }, 
    isLoading: isHistoricalLoading 
  } = useHistoricalResultsQuery({ date: today, fetchAll: false });
  
  // Safely get the historical data with a default empty object
  const historicalData = histResults?.data || {};

  // Update document title on component mount
  useEffect(() => {
    document.title = `Lexi Tips - Daily Wrap`;
  }, []);

  // Calculate wins and places when historical data is available
  useEffect(() => {
    // If params are provided, use those values
    if (paramWins && paramPlaces) {
      setWinsCount(paramWins);
      setPlacesCount(paramPlaces);
      return;
    }

    // Otherwise, calculate from historical data
    if (Object.keys(historicalData).length > 0) {
      const tally = tallyResultsForToday(historicalData, 2);
      setWinsCount(String(tally.total.wins));
      setPlacesCount(String(tally.total.places));
    }
  }, [historicalData, paramWins, paramPlaces]);

  // Fetch screenshot if not provided in URL params
  useEffect(() => {
    async function fetchHomeScreenshot() {
      if (!paramScreenshotUrl) {
        try {
          setLoading(true);
          const result = await takeHomeScreenshot(previewToken);
          if (result.success) {
            setScreenshotSrc(result.screenshot);
          } else {
            console.error('Failed to get screenshot');
            // Use fallback image if screenshot fails
            setScreenshotSrc('');
          }
        } catch (error) {
          console.error('Error fetching home screenshot:', error);
          // Use fallback image on error
          setScreenshotSrc('');
        } finally {
          setLoading(false);
        }
      } else {
        // Use the provided URL
        setScreenshotSrc(paramScreenshotUrl);
      }
    }
    
    fetchHomeScreenshot();
  }, [paramScreenshotUrl]);

  // Handle image loading errors
  const handleImageError = () => {
    console.error('Failed to load screenshot image');
    setImgError(true);
  };

  // Render custom results tally components similar to ResultsTally
  const renderWinsDisplay = () => {
    return (
      <div className="flex items-center">
        <TrophyIcon fill="#F30099" className="w-10 h-10 mr-2" />
        <span className="font-bold">{winsCount}</span>
      </div>
    );
  };

  const renderPlacesDisplay = () => {
    return (
      <div className="flex items-center">
        <MedalIcon className="w-10 h-10 mr-2" />
        <span className="font-bold">{placesCount}</span>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');
          
          body, html {
            margin: 0;
            padding: 0;
            width: 1080px;
            height: 1080px;
            font-family: 'Inter', sans-serif;
            background-color: #FCEDF7;
            overflow: hidden;
          }
          
          .instagram-post {
            position: relative;
            width: 1080px;
            height: 1080px;
            background: #FCEDF7;
            overflow: hidden;
          }
          
          .wrap-title {
            position: absolute;
            width: 1080px;
            height: 115px;
            left: 0px;
            top: 105px;
            font-style: italic;
            font-weight: 900;
            font-size: 83px;
            line-height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            text-transform: uppercase;
            color: #F30099;
            z-index: 20;
          }
          
          .stats-container {
            position: absolute;
            right: 80px;
            top: 520px;
            z-index: 20;
          }
          
          .wins {
            margin-right: 105px;
            font-style: italic;
            font-weight: 900;
            font-size: 80px;
            line-height: 80px;
            text-align: right;
            color: #000000;
            display: flex;
            align-items: center;
            justify-content: flex-end;
          }
          
          .places {
            margin-right: 105px;
            font-style: italic;
            font-weight: 900;
            font-size: 64px;
            line-height: 80px;
            text-align: right;
            color: #000000;
            margin-top: 20px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
          }
          
          .branding {
            position: absolute;
            width: 252px;
            height: 58px;
            right: 80px;
            bottom: 80px;
            z-index: 20;
          }
          
          /* Apple Template Styles */
          .apple-template-container {
            position: absolute;
            width: 620.72px;
            height: 1270.06px;
            left: -50px;
            top: 400px;
            transform: rotate(3.92deg);
            z-index: 5;
          }

          .apple-template-img {
            position: absolute;
            width: auto;
            height: auto;
            left: 0;
            top: -50px;
            z-index: 5;
            pointer-events: none;
          }

          /* Adjusted position for the screenshot */
          .apple-screenshot {
            transform: rotate(3.92deg);
            position: absolute;
            width: 448.71px;
            height: 972.43px;
            left: 38.37px;
            top: 20px;
            z-index: 15;
            overflow: hidden;
            // border-top-left-radius: 30px;
            // border-top-right-radius: 30px;
            transform-origin: top left;
          }
          
          .screenshot-img {
            width: 100%;
            height: auto;
            display: block;
          }
          
          .error-img {
            background-color: #e0e0e0;
          }
        `}
      </style>
      <div className="instagram-post">
        <h2 className="wrap-title">LEXI'S DAILY WRAP</h2>
        
        <div className="stats-container">
          <div className="wins">{winsCount} WINS</div>
          <div className="places">{placesCount} Places</div>
        </div>
        
        {/* Apple Template Container */}
        <div className="apple-template-container">
          {/* Phone template image */}
          <img 
            src="https://lexi.tips/images/daily_wrap_apple.png"
            alt="Apple Template" 
            className="apple-template-img" 
            id="apple-template" 
            crossOrigin="anonymous"
          />
          
          {/* Screenshot image positioned on top with higher z-index */}
          <div className="apple-screenshot">
            {loading || (isHistoricalLoading && (!paramWins || !paramPlaces)) ? (
              <div style={{ 
                background: '#f0f0f0',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <p>Loading...</p>
              </div>
            ) : (
              <img
                src={screenshotSrc}
                alt="Lexi Tips Preview"
                className={`screenshot-img ${imgError ? 'error-img' : ''}`}
                onError={handleImageError}
                crossOrigin="anonymous"
              />
            )}
          </div>
        </div>
        
        <div className="branding">
          <img 
            src="https://lexi.tips/lexi-tips-logo.svg" 
            alt="Lexi.tips" 
            id="lexi-logo" 
            width="auto"
            height="auto"
            crossOrigin="anonymous"
          />
        </div>
      </div>
    </>
  );
}

export default DailyWrap; 